import {
  Scoreboard,
  ScoreboardParticipant,
  ScoreboardParticipants,
  ScoreboardScore,
  ScoreboardScores
} from '@ps/isp-web-component-library';
import type { EnrichedParticipants, EventId } from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import type {
  BasketballFixture,
  BasketballPeriod,
  BasketballPeriodScore
} from '@ps/web-shared-generated-graphql/sports';
import React from 'react';

import { EventTypeId } from '@swf/sports/types/eventType';
import { getScoreCharacterLength } from '@swf/sports/utils/getScoreCharacterLength';

import ScoreboardWrapper from '../ScoreboardWrapper/ScoreboardWrapper';

interface Props {
  enrichedParticipants?: EnrichedParticipants;
  eventId: EventId;
  eventTime: string;
  basketballFixture?: BasketballFixture;
}

const halfPeriods = ['PERIOD_1', 'PERIOD_2'] as const satisfies BasketballPeriod[];

const quarterPeriods = [
  ...halfPeriods,
  'PERIOD_3',
  'PERIOD_4'
] as const satisfies BasketballPeriod[];

const isBasketballPeriodScore = (
  periodScore: BasketballPeriodScore | null
): periodScore is BasketballPeriodScore =>
  notEmpty(periodScore) && notEmpty(periodScore.period) && notEmpty(periodScore.score);

const isHalfBasedGame = (clock: BasketballFixture['clock']) =>
  ['H1', 'H2'].includes(clock?.segment ?? '');

const isQuarterBasedGame = (clock: BasketballFixture['clock']) =>
  ['Q1', 'Q2', 'Q3', 'Q4'].includes(clock?.segment ?? '');

const getPeriodScores = (
  periodScores: BasketballFixture['periodScores'],
  clock: BasketballFixture['clock']
): BasketballPeriodScore[] => {
  let periods: BasketballPeriod[] = [];

  // leave periods empty if we can't determine the game type to display all periods scores
  if (isHalfBasedGame(clock)) {
    periods = halfPeriods;
  } else if (isQuarterBasedGame(clock)) {
    periods = quarterPeriods;
  }

  const standardPeriodScores = periods.map(
    (period) => periodScores?.find((qs) => qs?.period === period) ?? { period }
  );

  const additionalPeriodScores =
    periodScores?.filter((ps) => !standardPeriodScores.find((sp) => sp.period === ps?.period)) ??
    [];

  return [...standardPeriodScores, ...additionalPeriodScores.filter(isBasketballPeriodScore)];
};

const getParticipantScore = (
  participant: EnrichedParticipants[number],
  score: BasketballFixture['score'] | undefined
) => (participant.type === 'HOME' ? score?.home : score?.away);

const Basketball: React.FC<Props> = ({
  enrichedParticipants,
  basketballFixture,
  eventId,
  eventTime
}) => {
  const [participantA, participantB] = enrichedParticipants ?? [];

  const currentScore = participantA.type && participantB.type ? basketballFixture?.score : null;
  const periodScores =
    participantA.type && participantB.type ? basketballFixture?.periodScores : null;
  const clock = basketballFixture?.clock;
  const hasPeriodScores = notEmpty(periodScores);
  const allPeriodScores = getPeriodScores(periodScores, clock);

  return (
    <ScoreboardWrapper eventId={eventId} eventTime={eventTime} eventTypeId={EventTypeId.BASKETBALL}>
      <Scoreboard>
        <ScoreboardParticipants>
          <ScoreboardParticipant name={participantA.participant} />
          <ScoreboardParticipant name={participantB.participant} />
        </ScoreboardParticipants>
        <ScoreboardScores>
          {hasPeriodScores
            ? allPeriodScores.map((periodScore) => (
                <ScoreboardScore
                  key={periodScore.period}
                  score={[
                    getParticipantScore(participantA, periodScore.score) ?? null,
                    getParticipantScore(participantB, periodScore.score) ?? null
                  ]}
                />
              ))
            : null}
          <ScoreboardScore
            score={[
              getParticipantScore(participantA, currentScore) ?? null,
              getParticipantScore(participantB, currentScore) ?? null
            ]}
            scoreCharacterLength={getScoreCharacterLength(EventTypeId.BASKETBALL, [
              currentScore?.home ?? 0,
              currentScore?.away ?? 0
            ])}
          />
        </ScoreboardScores>
      </Scoreboard>
    </ScoreboardWrapper>
  );
};

export default Basketball;
